import * as React from 'react';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer, { drawerClasses } from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MenuContent from './MenuContent';
import { authFetch } from '../authFetch';
// --- MODIFICATION: Import the simulation context hook ---
import { useSimulation } from './contexts/SimulationContext';


function SideMenuMobile({ open, toggleDrawer }) {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState({
    username: '',
    email: '',
    profile_picture: null,
  });

  // --- MODIFICATION: Get the navigation prompter from the context ---
  const { promptNavigation } = useSimulation();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await authFetch('/api/user/profile/');
        const data = await response.json();
        setUserProfile(data);
      } catch (error) {
        console.error("Failed to fetch user profile for side menu:", error);
      }
    };

    if (open) {
      fetchProfile();
    }

    // Re-fetch whenever SettingsTab signals a profile change, even if drawer is closed
    window.addEventListener('profileUpdated', fetchProfile);
    return () => window.removeEventListener('profileUpdated', fetchProfile);
  }, [open]);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    navigate('/signin');
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={toggleDrawer(false)}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        [`& .${drawerClasses.paper}`]: {
          backgroundImage: 'none',
          backgroundColor: 'background.paper',
        },
      }}
    >
      <Box
        sx={{
          maxWidth: '70dvw',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack sx={{ flexGrow: 1, overflow: 'auto' }}>
          {/* --- MODIFICATION: Pass the navigation handler as a prop --- */}
          <MenuContent onNavigate={promptNavigation} />
        </Stack>
        <Stack
          direction="row"
          sx={{
            p: 2,
            gap: 1,
            alignItems: 'center',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Avatar
            sizes="small"
            alt={userProfile.username}
            src={userProfile.profile_picture}
            sx={{ width: 36, height: 36 }}
          >
            {userProfile.username && userProfile.username.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ mr: 'auto', minWidth: 0 }}>
            <Typography
              noWrap
              variant="body2"
              sx={{
                fontWeight: 500,
                lineHeight: '16px',
              }}
            >
              {userProfile.username || 'Loading...'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {userProfile.email}
            </Typography>
          </Box>
          <IconButton onClick={handleLogout} aria-label="logout">
            <LogoutRoundedIcon />
          </IconButton>
        </Stack>
      </Box>
    </Drawer>
  );
}

SideMenuMobile.propTypes = {
  open: PropTypes.bool,
  toggleDrawer: PropTypes.func.isRequired,
};

export default SideMenuMobile;